import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from './PartnerListChrome';
import { getPartnerSessionFromToken } from '../utils/partnerPush';

function DetailField({ label, hint, children }) {
  if (children == null || children === '') return null;
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '0.11em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink3,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {hint ? (
        <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3, mb: 0.5, fontStyle: 'italic' }}>
          {hint}
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
}

const PartnerTripRequestDrawer = ({ open, requestId, onClose, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const partnerSession = useMemo(() => getPartnerSessionFromToken(), []);

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolutionText, setResolutionText] = useState('');

  const loadRequest = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/partners/requests/${requestId}`, { usePartnerAuth: true });
      const next = response.data?.request || null;
      setRequest(next);
      setInternalNotes(next?.internalNotes || '');
      setAssigneeId(next?.assignedUserId ? String(next.assignedUserId) : '');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerOps.loadFailed'));
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, t]);

  const loadTeamMembers = useCallback(async () => {
    try {
      const response = await api.get('/partners/team-members', { usePartnerAuth: true });
      const users = Array.isArray(response.data) ? response.data : (response.data?.users || []);
      setTeamMembers(users);
    } catch {
      setTeamMembers([]);
    }
  }, []);

  useEffect(() => {
    if (!open || !requestId) {
      setRequest(null);
      setError('');
      setResolveOpen(false);
      setResolutionText('');
      return;
    }
    loadRequest();
    loadTeamMembers();
  }, [open, requestId, loadRequest, loadTeamMembers]);

  const refresh = async () => {
    await loadRequest();
    onChanged?.();
  };

  const runAction = async (fn) => {
    try {
      setActionLoading(true);
      setError('');
      await fn();
      await refresh();
    } catch (err) {
      const message = err.response?.data?.error || t('partnerOps.actionFailed');
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = () => runAction(async () => {
    let assignedUserId = assigneeId ? Number(assigneeId) : null;
    if (!teamMembers.length) {
      assignedUserId = partnerSession.partnerUserId || null;
    } else if (!assignedUserId && teamMembers.length === 1) {
      assignedUserId = Number(teamMembers[0].id);
    }
    await api.post(
      `/partners/requests/${requestId}/assign`,
      { assignedUserId },
      { usePartnerAuth: true }
    );
  });

  const handleStart = () => runAction(async () => {
    await api.patch(
      `/partners/requests/${requestId}`,
      { status: 'in_progress' },
      { usePartnerAuth: true }
    );
  });

  const handleAwaitSupplier = () => runAction(async () => {
    await api.patch(
      `/partners/requests/${requestId}`,
      { status: 'awaiting_supplier' },
      { usePartnerAuth: true }
    );
  });

  const handleAwaitTraveler = () => runAction(async () => {
    await api.patch(
      `/partners/requests/${requestId}`,
      { status: 'awaiting_traveler' },
      { usePartnerAuth: true }
    );
  });

  const handleResolve = () => runAction(async () => {
    await api.post(
      `/partners/requests/${requestId}/resolve`,
      { resolution: resolutionText.trim() || undefined },
      { usePartnerAuth: true }
    );
    setResolveOpen(false);
    setResolutionText('');
  });

  const handleReopen = () => runAction(async () => {
    await api.post(`/partners/requests/${requestId}/reopen`, {}, { usePartnerAuth: true });
  });

  const handleSaveNotes = async () => {
    try {
      setNotesSaving(true);
      setError('');
      await api.patch(
        `/partners/requests/${requestId}`,
        { internalNotes },
        { usePartnerAuth: true }
      );
      await refresh();
      enqueueSnackbar(t('partnerOps.notesSaved'), { variant: 'success' });
    } catch (err) {
      setError(err.response?.data?.error || t('partnerOps.actionFailed'));
    } finally {
      setNotesSaving(false);
    }
  };

  const handleViewTrip = () => {
    if (!request?.tripId) return;
    onClose?.();
    navigate(`/partner/trips/${request.tripId}?tab=operations`);
  };

  const handleReply = () => {
    if (!request?.tripId) return;
    onClose?.();
    navigate(`/partner/trips/${request.tripId}?tab=operations`);
  };

  const handleUpdateItinerary = () => {
    if (!request?.tripId) return;
    const params = new URLSearchParams({ tab: 'itinerary' });
    if (request.itineraryItemId) {
      params.set('focusItem', String(request.itineraryItemId));
    }
    onClose?.();
    navigate(`/partner/trips/${request.tripId}?${params.toString()}`);
  };

  const formatEventTime = (value) => {
    if (!value) return '';
    try {
      return format(parseISO(String(value).replace(' ', 'T')), 'MMM d, yyyy h:mm a');
    } catch {
      return String(value);
    }
  };

  const isResolved = request?.status === 'resolved' || request?.status === 'cancelled';

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          'data-testid': 'partner-trip-request-drawer',
          sx: {
            width: { xs: '100%', sm: 560, md: 640 },
            maxWidth: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            px: { xs: 2, md: 3.5 },
            pt: { xs: 'calc(12px + env(safe-area-inset-top, 0px))', md: 3 },
            pb: 1.5,
            borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, pt: 0.5 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26 }}>
              {request
                ? [request.clientName, request.destination || request.tripTitle].filter(Boolean).join(' — ')
                : t('partnerOps.requestDetailTitle')}
            </Typography>
            {request?.title ? (
              <Typography sx={{ mt: 1, fontSize: 16, fontStyle: 'italic', color: DOCUMENT.ink2 }}>
                “{request.title}”
              </Typography>
            ) : null}
            {request?.status ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: request.status === 'resolved' ? '#2E7D32' : '#F9A825',
                  }}
                />
                <Typography sx={{ fontSize: 13 }}>
                  {t(`partnerOps.status.${request.status}`, { defaultValue: request.status })}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <IconButton
            aria-label={t('common.close')}
            onClick={onClose}
            sx={{ color: DOCUMENT.ink, mt: 0.25 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 3.5 }, py: 2.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}

          {request && !loading ? (
            <>
              <DetailField label={t('partnerOps.fieldClient')}>{request.clientName}</DetailField>
              <DetailField label={t('partnerOps.fieldTrip')}>{request.tripTitle}</DetailField>
              <DetailField label={t('partnerOps.fieldType')}>
                {t(`partnerOps.requestType.${request.type}`, { defaultValue: request.type })}
              </DetailField>
              <DetailField label={t('partnerOps.fieldSource')}>
                {t(`partnerOps.source.${request.source}`, { defaultValue: request.source })}
              </DetailField>
              <DetailField label={t('partnerOps.fieldOwner')}>
                {request.assignedUserName || t('partnerOps.unassigned')}
              </DetailField>
              <DetailField label={t('partnerOps.fieldAge')}>{request.age}</DetailField>
              <DetailField label={t('partnerOps.fieldStatus')}>
                {t(`partnerOps.status.${request.status}`, { defaultValue: request.status })}
              </DetailField>
              <DetailField label={t('partnerOps.fieldRelatedStop')}>
                {request.itineraryItemName}
              </DetailField>
              <DetailField label={t('partnerOps.fieldDescription')}>{request.description}</DetailField>

              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: '0.11em',
                    textTransform: 'uppercase',
                    color: DOCUMENT.ink3,
                    mb: 0.5,
                  }}
                >
                  {t('partnerOps.fieldInternalNotes')}
                </Typography>
                <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3, mb: 1, fontStyle: 'italic' }}>
                  {t('partnerOps.fieldInternalNotesHint')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={internalNotes}
                  onChange={(event) => setInternalNotes(event.target.value)}
                  placeholder={t('partnerOps.internalNotesPlaceholder')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
                <Button
                  size="small"
                  onClick={handleSaveNotes}
                  disabled={notesSaving}
                  sx={{ ...partnerOutlineBtnSx, mt: 1 }}
                >
                  {notesSaving ? t('partnerLists.loadingShort') : t('partnerOps.saveNotes')}
                </Button>
              </Box>

              {request.events?.length ? (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${DOCUMENT.ruleHard}` }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: '0.11em',
                      textTransform: 'uppercase',
                      color: DOCUMENT.ink3,
                      mb: 1.5,
                    }}
                  >
                    {t('partnerOps.resolutionHistory')}
                  </Typography>
                  {request.events.map((event) => {
                    const fromWhatsapp = event.eventType === 'whatsapp_reply'
                      || event.eventType === 'whatsapp_note';
                    return (
                    <Box
                      key={event.id}
                      sx={{
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: `1px solid ${DOCUMENT.rule}`,
                        ...(fromWhatsapp ? {
                          pl: 1.25,
                          borderLeft: `3px solid ${DOCUMENT.oxblood}`,
                        } : {}),
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: DOCUMENT.ink }}>
                        {fromWhatsapp ? (
                          <Box
                            component="span"
                            sx={{
                              mr: 0.75,
                              fontSize: 10,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: DOCUMENT.oxblood,
                            }}
                          >
                            {t('partnerOps.source.whatsapp')}
                          </Box>
                        ) : null}
                        {t(`partnerOps.eventType.${event.eventType}`, { defaultValue: event.eventType })}
                        {event.toStatus
                          ? ` → ${t(`partnerOps.status.${event.toStatus}`, { defaultValue: event.toStatus })}`
                          : ''}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
                        {[event.actorName, formatEventTime(event.createdAt)].filter(Boolean).join(' · ')}
                      </Typography>
                      {event.note ? (
                        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {event.note}
                        </Typography>
                      ) : null}
                    </Box>
                    );
                  })}
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>

        {request && !loading ? (
          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 2, md: 3.5 },
              py: 2,
              borderTop: `1px solid ${DOCUMENT.ruleHard}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {teamMembers.length > 1 ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="request-assignee-label">{t('partnerOps.fieldOwner')}</InputLabel>
                <Select
                  labelId="request-assignee-label"
                  label={t('partnerOps.fieldOwner')}
                  value={assigneeId}
                  onChange={(event) => setAssigneeId(event.target.value)}
                  sx={{ borderRadius: 0 }}
                >
                  <MenuItem value="">
                    <em>{t('partnerOps.unassigned')}</em>
                  </MenuItem>
                  {teamMembers.map((member) => (
                    <MenuItem key={member.id} value={String(member.id)}>
                      {member.name || member.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {request.tripId ? (
                <Button size="small" onClick={handleViewTrip} sx={partnerOutlineBtnSx}>
                  {t('partnerOps.actions.viewTrip')}
                </Button>
              ) : null}
              {request.tripId ? (
                <Button size="small" onClick={handleReply} sx={partnerOutlineBtnSx}>
                  {t('partnerOps.actions.reply')}
                </Button>
              ) : null}
              {request.tripId ? (
                <Button size="small" onClick={handleUpdateItinerary} sx={partnerOutlineBtnSx}>
                  {t('partnerOps.actions.updateItinerary')}
                </Button>
              ) : null}
              {!isResolved ? (
                <Button
                  size="small"
                  disabled={actionLoading}
                  onClick={() => setResolveOpen(true)}
                  sx={partnerOxbloodBtnSx}
                >
                  {t('partnerOps.actions.resolve')}
                </Button>
              ) : (
                <Button
                  size="small"
                  disabled={actionLoading}
                  onClick={handleReopen}
                  sx={partnerOutlineBtnSx}
                >
                  {t('partnerOps.actions.reopen')}
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {!isResolved ? (
                <>
                  <Button
                    size="small"
                    disabled={actionLoading}
                    onClick={handleAssign}
                    sx={partnerOutlineBtnSx}
                  >
                    {t('partnerOps.assign')}
                  </Button>
                  {!['in_progress', 'awaiting_supplier', 'awaiting_traveler'].includes(request.status) ? (
                    <Button
                      size="small"
                      disabled={actionLoading}
                      onClick={handleStart}
                      sx={partnerOutlineBtnSx}
                    >
                      {t('partnerOps.actions.start')}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    disabled={actionLoading}
                    onClick={handleAwaitSupplier}
                    sx={partnerOutlineBtnSx}
                  >
                    {t('partnerOps.actions.awaitSupplier')}
                  </Button>
                  <Button
                    size="small"
                    disabled={actionLoading}
                    onClick={handleAwaitTraveler}
                    sx={partnerOutlineBtnSx}
                  >
                    {t('partnerOps.actions.awaitTraveler')}
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </Drawer>

      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {t('partnerOps.actions.resolve')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            autoFocus
            value={resolutionText}
            onChange={(event) => setResolutionText(event.target.value)}
            placeholder={t('partnerOps.resolvePlaceholder')}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveOpen(false)} sx={partnerOutlineBtnSx}>
            {t('partnerLists.cancel')}
          </Button>
          <Button
            onClick={handleResolve}
            disabled={actionLoading}
            sx={partnerOxbloodBtnSx}
          >
            {t('partnerOps.actions.resolve')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerTripRequestDrawer;
